<template>
  <!-- Portaled to <body>: the drawer's translateX would otherwise trap it. -->
  <BodyPortal>
    <transition name="pay-plan-dialog" appear>
      <div
        v-if="open"
        class="ppd__overlay"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        @mousedown.self="emit('close')"
      >
        <div class="ppd__card">
          <div class="ppd__header">
            <div class="ppd__header-top">
              <div class="ppd__featured" aria-hidden="true">
                <span class="ppd__featured-glyph">
                  <slot name="icon" />
                </span>
              </div>
              <button
                type="button"
                class="ppd__close-btn"
                aria-label="Close"
                :disabled="submitting"
                @click="emit('close')"
              >
                <Icon name="popup_close" />
              </button>
            </div>
            <div class="ppd__copy">
              <h2 :id="titleId" class="ppd__title">{{ title }}</h2>
              <p v-if="$slots.description" class="ppd__body">
                <slot name="description" />
              </p>
            </div>
          </div>

          <div v-if="$slots.default" class="ppd__body-section">
            <slot />
          </div>

          <div class="ppd__footer">
            <slot name="footer" />
          </div>
        </div>
      </div>
    </transition>
  </BodyPortal>
</template>

<script setup>
import BodyPortal from "@components/BodyPortal/BodyPortal.vue";
import Icon from "@components/icons/Icon.vue";

/**
 * The shared Fair Pay dialog shell (matches the app's modal, e.g.
 * UnsubscribeFromPayRangeDialog): overlay + 400px card, a featured icon tile,
 * title + description, a body section for fields, and a right-aligned footer.
 *
 * It portals itself to <body> because the Vuetify navigation drawer applies
 * `transform: translateX(...)`, which traps a `position: fixed` overlay inside
 * the drawer instead of centring it on the screen.
 */
defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    required: true,
  },
  /** Disables the close affordances while an action is in flight. */
  submitting: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["close"]);

// Stable id per title keeps aria-labelledby valid without a global counter.
const titleId = `pay-plan-dialog-title-${Math.random().toString(36).slice(2, 8)}`;
</script>

<style scoped>
.ppd__overlay {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(16, 24, 40, 0.5);
}

.ppd__card {
  position: relative;
  width: 400px;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 12px;
  background: #ffffff;
  box-shadow:
    0 20px 24px -4px rgba(16, 24, 40, 0.08),
    0 8px 8px -4px rgba(16, 24, 40, 0.03);
  font-family: Inter, sans-serif;
}

.ppd__header {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  padding: 24px 24px 0;
  box-sizing: border-box;
}

.ppd__header-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
}

.ppd__featured {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: #f9fafb;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  box-sizing: border-box;
}

.ppd__featured-glyph {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.ppd__close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin: -8px -8px 0 0;
  padding: 8px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #98a2b3;
  cursor: pointer;
  box-sizing: border-box;
}

.ppd__close-btn:hover:not(:disabled) {
  background: #f2f4f7;
  color: #667085;
}

.ppd__close-btn:disabled {
  cursor: default;
  opacity: 0.5;
}

.ppd__copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.ppd__title {
  margin: 0;
  color: #101828;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  overflow-wrap: break-word;
}

.ppd__body {
  margin: 0;
  color: #475467;
  font-size: 14px;
  line-height: 20px;
}

.ppd__body-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px 24px 0;
  box-sizing: border-box;
}

/* The shared checkbox capitalises its label; keep sentence casing here. */
.ppd__body-section :deep(.filter-checkbox .label) {
  text-transform: none;
  font-weight: 500;
}

.ppd__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px;
  box-sizing: border-box;
}

.pay-plan-dialog-enter-active,
.pay-plan-dialog-leave-active {
  transition: opacity 0.15s ease;
}

.pay-plan-dialog-enter,
.pay-plan-dialog-leave-to {
  opacity: 0;
}
</style>
